import { useRef, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faBoxOpen,
  faArrowUpRightFromSquare,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import './ProductCarousel.css';

// Render the structured product cards the agent attaches to a reply (delivered
// on the `products` SSE event) as a horizontally-scrollable gallery. Pure
// presentation: it reads the card shape produced by the backend
// (src/controller/products.py _product_card_from_product) — title, price,
// currency, image_url, url, availability, attributes — and never assumes any
// specific attribute keys, so it works for phones, dresses, bikes, anything.

const formatPrice = (price, currency) => {
  if (price === null || price === undefined || price === '') return null;
  const num = typeof price === 'number' ? price : parseFloat(price);
  if (Number.isNaN(num)) return null;
  const hasCents = Math.abs(num % 1) > 0.001;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

// "storage_gb" -> "storage", "frame_size_cm" -> "frame size". Drops a trailing
// unit suffix and underscores so the spec line reads naturally.
const prettifyKey = (k) =>
  String(k)
    .replace(/_(gb|tb|mb|mah|kg|g|cm|mm|inch|in|km|l|ml|ohm|bar|w|hz|mp|px)$/i, '')
    .replace(/_/g, ' ')
    .trim();

// A short, generic spec line: the first few attributes that aren't the brand
// (shown separately) or redundant with the title.
const buildSpecs = (attributes) => {
  if (!attributes || typeof attributes !== 'object') return [];
  const skip = new Set(['brand', 'title', 'name', 'product_name']);
  return Object.entries(attributes)
    .filter(([k, v]) => !skip.has(String(k).toLowerCase()) && v !== null && v !== '' && v !== undefined)
    .slice(0, 3)
    .map(([k, v]) => `${prettifyKey(k)} ${v}`.trim());
};

// Map a raw availability enum (IN_STOCK, OUT_OF_STOCK, PARTIALLY_OUT_OF_STOCK,
// backorder, ...) to a friendly, localized badge label. Known canonical states
// get a translated label; an unrecognized custom value (e.g. a CSV's own wording
// like "Auf Lager") is humanized — underscores to spaces, title-cased — so the
// badge never shows a raw machine enum. The enum itself is kept in the data
// (the agent filters/searches on it); this is presentation only.
const AVAILABILITY_KEYS = {
  in_stock: 'product_availability_in_stock',
  out_of_stock: 'product_availability_out_of_stock',
  partially_out_of_stock: 'product_availability_limited',
  backorder: 'product_availability_backorder',
  on_backorder: 'product_availability_backorder',
};

const formatAvailability = (value, t) => {
  if (value === null || value === undefined || value === '') return null;
  const norm = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  const key = AVAILABILITY_KEYS[norm];
  if (key) return t(key);
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
    .trim();
};

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const [imgError, setImgError] = useState(false);
  const brand = product.attributes && product.attributes.brand;
  const specs = buildSpecs(product.attributes);
  const price = formatPrice(product.price, product.currency);
  const availability = formatAvailability(product.availability, t);
  const href = product.url || product.product_url || null;
  const Tag = href ? 'a' : 'div';
  const linkProps = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : {};

  return (
    <Tag className={`ub-pc-card${href ? ' ub-pc-card-link' : ''}`} {...linkProps}>
      <div className="ub-pc-img">
        {product.image_url && !imgError ? (
          <img
            src={product.image_url}
            alt={product.title || ''}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="ub-pc-img-fallback">
            <FontAwesomeIcon icon={faBoxOpen} />
          </div>
        )}
        {availability && <span className="ub-pc-badge">{availability}</span>}
      </div>
      <div className="ub-pc-body">
        {brand && <span className="ub-pc-brand">{brand}</span>}
        <span className="ub-pc-title">{product.title || t('product_untitled')}</span>
        {specs.length > 0 && <span className="ub-pc-specs">{specs.join(' · ')}</span>}
        <div className="ub-pc-foot">
          {price && <span className="ub-pc-price">{price}</span>}
          {href && (
            <span className="ub-pc-cta">
              {t('product_view')}
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ub-pc-cta-ic" />
            </span>
          )}
        </div>
      </div>
    </Tag>
  );
};

const ProductCarousel = ({ products }) => {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const refresh = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    refresh();
    const el = trackRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    return () => {
      el.removeEventListener('scroll', refresh);
      window.removeEventListener('resize', refresh);
    };
  }, [refresh, products]);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.8, 220);
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  if (!Array.isArray(products) || products.length === 0) return null;

  return (
    <div className="ub-pc">
      <button
        type="button"
        className={`ub-pc-arrow ub-pc-arrow-l${canLeft ? '' : ' ub-pc-arrow-off'}`}
        onClick={() => scroll(-1)}
        aria-label={t('scroll_left')}
        tabIndex={canLeft ? 0 : -1}
      >
        <FontAwesomeIcon icon={faChevronLeft} />
      </button>

      <div className="ub-pc-track" ref={trackRef}>
        {products.map((p, i) => (
          <ProductCard key={p.id || p.url || i} product={p} />
        ))}
      </div>

      <button
        type="button"
        className={`ub-pc-arrow ub-pc-arrow-r${canRight ? '' : ' ub-pc-arrow-off'}`}
        onClick={() => scroll(1)}
        aria-label={t('scroll_right')}
        tabIndex={canRight ? 0 : -1}
      >
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  );
};

export default ProductCarousel;
